import { z } from 'zod';
import { McpUserTokenSourceSchema } from './formant-user-token.js';
/**
 * The two first-party built-ins that connect with a Formant login rather than
 * OAuth: Formant Robotics (`formant_telemetry`) and Formant Metaphysics
 * (`formant_metaphysics`). Shared between cortex-api (the connect flow) and
 * the persistent-agent host (per-user credential routing on chat threads).
 */
export const formantUserTokenCategories = ['formant_telemetry', 'formant_metaphysics'];
export function isFormantUserTokenCategory(category) {
    return !!category && formantUserTokenCategories.includes(category);
}
/**
 * The credential a chat sender's own login lends to the turn they start.
 *
 * Why this exists: the org keyring behind a Formant built-in holds the token
 * of whoever clicked Connect — usually an admin — and the shared chat agent
 * (`agt_formant_chat`) unions every connected MCP into every thread. Without
 * this, everyone chatting in the org ran Formant tools as that admin. So on
 * every user send to a CHAT thread, cortex-api stashes the sender's *own*
 * bearer here (Vault, keyed by org + subject), and the host routes that
 * turn's Formant tool calls through it instead of the org keyring. Fleet
 * agents and the console sidebar keep using the org keyring unchanged.
 *
 * Deliberately NOT a long-lived connection: no refresh token, no refresh
 * sweep. It is exactly the caller's live session token and lapses with it —
 * a turn that runs after the sender's login expired gets a clear tool error
 * asking for a new message, never a fallback to someone else's identity.
 */
export const ChatActorCredentialSchema = z.object({
    /** Auth subject of the sender (the `user:<subject>` event provenance). */
    subject: z.string().min(1),
    /** Which login minted the token — decides which built-ins accept it. */
    tokenSource: McpUserTokenSourceSchema,
    /** The sender's live bearer, stored verbatim. */
    accessToken: z.string().min(1),
    /** ISO `exp` of the bearer when decodable; null = opaque, trusted until rejected. */
    expiresAt: z.string().nullable(),
    /** When cortex-api stashed it (ISO). */
    stashedAt: z.string(),
});
/** Vault KV path (relative to the mount) of one sender's stashed credential. */
export function chatActorCredentialVaultPath(orgId, subject) {
    return `box/chat-actor-credential/org/${encodeURIComponent(orgId)}/user/${encodeURIComponent(subject)}`;
}
/**
 * Whether a stashed credential can authenticate to a given built-in.
 * Formant Robotics only accepts Formant (Coherence/telemetry) tokens — a
 * Keycloak login's token is rejected there (the connect flow runs OAuth for
 * that case instead). Metaphysics is dual-issuer and accepts both.
 */
export function chatActorCredentialUsableFor(credential, category) {
    if (!isFormantUserTokenCategory(category))
        return false;
    if (credential.tokenSource === 'robotics')
        return true;
    return category === 'formant_metaphysics';
}
/** True when the bearer's recorded `exp` has passed (with a small leeway). */
export function chatActorCredentialExpired(credential, now = Date.now(), leewayMs = 10_000) {
    if (!credential.expiresAt)
        return false;
    const exp = Date.parse(credential.expiresAt);
    if (Number.isNaN(exp))
        return false;
    return exp - leewayMs <= now;
}
/**
 * The tool-result envelope a chat turn gets when no usable sender credential
 * exists for a Formant built-in — an MCP error envelope (`isError: true`) so
 * the transcript, tool-usage accounting and the model all see a failed call
 * with an actionable reason, rather than an opaque upstream 401.
 */
export function chatActorCredentialErrorEnvelope(reason, connectionName) {
    return {
        isError: true,
        content: [{ type: 'text', text: chatActorCredentialErrorText(reason, connectionName) }],
    };
}
export function chatActorCredentialErrorText(reason, connectionName) {
    switch (reason) {
        case 'no_subject':
            return `${connectionName} tools on chat threads run as the person who sent the message, and this turn has no sender. Ask the user to send a message and retry.`;
        case 'missing':
            return `${connectionName} tools on chat threads run as the person who sent the message, but no credential is stashed for them. Ask the user to send a new message (this re-stashes their login) and retry.`;
        case 'expired':
            return `The sender's ${connectionName} login has expired. Ask the user to send a new message (this refreshes their login) and retry.`;
        case 'wrong_login':
            return `${connectionName} only accepts a Formant login, and the sender is signed in another way. Tell the user to use Formant Robotics tools from a Formant login.`;
    }
}
//# sourceMappingURL=chat-actor-credential.js.map